import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
// Path imports, not the @mui/icons-material barrel (see EmailPreviewDialog).
import Download from '@mui/icons-material/Download';
import AttachFile from '@mui/icons-material/AttachFile';
import { QuoteExcel } from './useQuoteExcel';

interface ExcelAutomationPanelProps {
  excel: QuoteExcel;
  allItemsValidated: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
}

/** The customer's stored Excel files and the "fill them with prices" control. */
export default function ExcelAutomationPanel({
  excel,
  allItemsValidated,
  expanded,
  onToggleExpanded,
}: ExcelAutomationPanelProps) {
  const {
    excelFiles,
    updatedFiles,
    isRunningAutomation,
    isLoadingExcelFiles,
    automationStartTime,
    lastAutomationDuration,
    elapsedTime,
    automationJobId,
  } = excel;
  const storedFileCount = excelFiles.filter(file => file.exists && file.source === 'database').length;
  if (storedFileCount === 0) return null;

  return (
    <Box sx={{ px: 2, py: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" component="h3">Excel Automation</Typography>
        <Button
          size="small"
          onClick={onToggleExpanded}
        >
          {expanded ? 'Hide' : 'Show'} ({storedFileCount} files{updatedFiles.size > 0 ? `, ${updatedFiles.size} updated` : ''})
        </Button>
      </Box>

      {expanded && (
        <>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 1.5 }}>
            Update Excel files with validated prices and supplier remarks. Click "Update Excel Files with Prices" before sending your email to ensure latest pricing and remarks.
          </Typography>

          {/* Excel Files List */}
          <Paper variant="outlined" sx={{ mb: 1.5 }}>
            <List dense disablePadding>
              {excelFiles.map((file, index) => (
                <ListItem
                  key={index}
                  sx={{ flexWrap: 'wrap', columnGap: 1, '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' } }}
                >
                  <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', wordBreak: 'break-word' }}>
                          <span>{file.filename}</span>
                          {updatedFiles.has(file.filename) && (
                            <Chip
                              label="Updated"
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={`${file.size ? Math.round(file.size / 1024) + 'KB' : 'Unknown size'} • ${file.exists ? (updatedFiles.has(file.filename) ? 'Updated' : 'Available') : 'Missing'}`}
                  />
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                      <Button
                        size="small"
                        startIcon={<Download />}
                        onClick={() => excel.downloadExcelFile(file.filename)}
                        disabled={!file.exists}
                      >
                        Download
                      </Button>
                      <Button
                        size="small"
                        startIcon={<AttachFile />}
                        onClick={() => excel.attachExcelFile(file.filename)}
                        disabled={!file.exists}
                      >
                        Attach
                      </Button>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Paper>

          {/* Automation Controls */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => excel.runExcelAutomation()}
              disabled={!allItemsValidated || isRunningAutomation || isLoadingExcelFiles}
              startIcon={isRunningAutomation ? <CircularProgress size={20} color="inherit" /> : undefined}
              sx={{ minHeight: 40, fontWeight: 600 }}
            >
              {isRunningAutomation ? 'Processing...' : 'Update Excel Files with Prices'}
            </Button>

            <Button
              size="small"
              variant="outlined"
              onClick={excel.loadExcelFiles}
              disabled={isLoadingExcelFiles}
            >
              {isLoadingExcelFiles ? 'Loading...' : 'Refresh Files'}
            </Button>
          </Box>

          {/* Timing and Status Information */}
          {(lastAutomationDuration || automationStartTime) && (
            <Box sx={{ mt: 1.5, px: 1.5, py: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              {automationStartTime && (
                <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium' }}>
                  ⏱️ Processing Excel files... ({elapsedTime}s elapsed)
                </Typography>
              )}
              {lastAutomationDuration && !automationStartTime && (
                <Typography variant="body2" color="success.main" sx={{ fontWeight: 'medium' }}>
                  ✅ Last automation completed in {(lastAutomationDuration / 1000).toFixed(1)}s
                </Typography>
              )}
              {updatedFiles.size > 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                  📄 {updatedFiles.size} file(s) updated with validated prices
                </Typography>
              )}
            </Box>
          )}

          {!allItemsValidated && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              All item prices must be validated before running Excel automation.
            </Alert>
          )}

          {allItemsValidated && updatedFiles.size === 0 && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              💡 <strong>Recommended workflow:</strong> Click "Update Excel Files with Prices" first, then send your email to ensure clients receive files with the latest pricing and supplier remarks.
            </Alert>
          )}

          {automationJobId && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Automation job {automationJobId} is running. Updated files will appear in the list above.
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}
